import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Download, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import { ladeDateiHerunter } from '@libero/core/lib/download';
import { BASIS, type BogenOptionen, type Einstellungen, type Position } from '../lib/typen';
import { fehlerText } from '../lib/dateien';

interface Props {
  titel: string;
  positionen: Position[];
  einstellungen: Einstellungen;
  optionen: BogenOptionen;
  onOptionen: (o: BogenOptionen) => void;
  onHeruntergeladen: () => void;
}

interface Vorschau {
  svg: string;
  breiteMm: number;
  hoeheMm: number;
  anzahlInitialen: number;
  warnungen: string[];
}

const ZAHLFELDER: { key: keyof Einstellungen['grenzen']; label: string }[] = [
  { key: 'buchstabenHoeheMm', label: 'Buchstabenhöhe (mm)' },
  { key: 'bogenBreiteMm', label: 'Bogenbreite (mm)' },
  { key: 'abstandMm', label: 'Abstand (mm)' },
  { key: 'randMm', label: 'Rand (mm)' },
];

export default function DruckbogenPanel({ titel, positionen, einstellungen, optionen, onOptionen, onHeruntergeladen }: Props) {
  const [vorschau, setVorschau] = useState<Vorschau | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [laedtDownload, setLaedtDownload] = useState(false);
  const anfrage = useRef(0);

  const relevant = positionen.filter((p) => p.initialen.trim());
  const body = { titel, positionen: relevant, optionen };
  const schluessel = JSON.stringify(body);

  // Vorschau mit Debounce nachziehen, wenn sich Liste oder Optionen ändern
  useEffect(() => {
    if (!relevant.length) { setVorschau(null); return; }
    const nr = ++anfrage.current;
    const t = setTimeout(async () => {
      setLaedt(true);
      try {
        const r = await api.post(`${BASIS}/druckbogen`, body, { params: { format: 'json' } });
        if (nr === anfrage.current) setVorschau(r.data.data);
      } catch (e) {
        if (nr === anfrage.current) toast.error(await fehlerText(e, 'Vorschau fehlgeschlagen'));
      } finally {
        if (nr === anfrage.current) setLaedt(false);
      }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel]);

  async function herunterladen() {
    setLaedtDownload(true);
    try {
      const r = await api.post(`${BASIS}/druckbogen`, body, { responseType: 'blob' });
      const cd = String(r.headers?.['content-disposition'] ?? '');
      const name = cd.match(/filename="([^"]+)"/)?.[1] ?? 'Druckbogen.svg';
      ladeDateiHerunter(r.data as Blob, name, 'svg');
      onHeruntergeladen();
    } catch (e) {
      toast.error(await fehlerText(e, 'Download fehlgeschlagen'));
    } finally {
      setLaedtDownload(false);
    }
  }

  return (
    <div className="space-y-3 rounded-lg border border-[#DAD9D9] bg-white p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[#1A1A1A]">Druckbogen für CorelDRAW</h2>
        <Button onClick={herunterladen} disabled={!relevant.length || laedtDownload} className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]">
          {laedtDownload ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
          SVG herunterladen
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {ZAHLFELDER.map((f) => (
          <label key={f.key} className="text-xs text-gray-600">
            {f.label}
            <input
              type="number"
              min={einstellungen.grenzen[f.key].min}
              max={einstellungen.grenzen[f.key].max}
              value={optionen[f.key]}
              onChange={(e) => onOptionen({ ...optionen, [f.key]: Number(e.target.value) })}
              className="mt-1 w-full rounded border border-[#DAD9D9] px-2 py-1 text-sm text-[#1A1A1A]"
            />
          </label>
        ))}
        <label className="text-xs text-gray-600">
          Schrift
          <select value={optionen.schrift} onChange={(e) => onOptionen({ ...optionen, schrift: e.target.value })}
            className="mt-1 w-full rounded border border-[#DAD9D9] px-2 py-1 text-sm text-[#1A1A1A]">
            {einstellungen.schriften.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </label>
        <label className="flex items-end gap-2 pb-1 text-sm text-[#1A1A1A]">
          <input type="checkbox" className="h-4 w-4 accent-[#1A1A1A]" checked={optionen.groessenLabels}
            onChange={(e) => onOptionen({ ...optionen, groessenLabels: e.target.checked })} />
          Größen-Labels
        </label>
      </div>

      {vorschau && (
        <p className="text-sm text-gray-600">
          {vorschau.anzahlInitialen} Initialen · Bogen {(vorschau.breiteMm / 10).toLocaleString('de-DE')} × {(vorschau.hoeheMm / 10).toLocaleString('de-DE', { maximumFractionDigits: 1 })} cm
          {laedt && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" />}
        </p>
      )}
      {vorschau?.warnungen.map((w) => (
        <p key={w} className="flex items-center gap-1 text-sm text-amber-700"><AlertTriangle className="h-4 w-4" /> {w}</p>
      ))}

      <div className="max-h-[70vh] overflow-auto rounded border border-[#DAD9D9] bg-[repeating-conic-gradient(#f4f4f4_0_25%,#fff_0_50%)] bg-[length:20px_20px] p-2">
        {vorschau ? (
          // SVG kommt aus unserem eigenen Generator (nur <path>-Elemente)
          <div className="[&>svg]:h-auto [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: vorschau.svg.replace(/^<\?xml[^>]*>\s*/, '') }} />
        ) : (
          <p className="p-6 text-center text-sm text-gray-400">{laedt ? 'Vorschau wird erstellt …' : 'Noch keine Initialen in der Liste.'}</p>
        )}
      </div>

      <p className="text-xs text-gray-500">
        In CorelDRAW: Datei → Importieren (Strg+I), Maßstab 1:1. Initialen liegen schon als Kurven vor.
        Die roten Größen-Labels (Gruppe „Hilfstexte_nicht_plotten“) vor dem Plotten löschen; Spiegeln für Flex/Flock im Plotter-Dialog.
      </p>
    </div>
  );
}
